import { type ReactNode } from 'react'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import { Card, CardContent } from '@/components/Card'
import cx from 'classnames'
import styles from './FirmDashboardQuickSelectFilter.module.css'
import { type JobStatusFilterOption } from '@/features/jobs/list/job-status-filter-option'
import { match } from '@/utils/pattern-match-util'

export interface FirmDashboardQuickSelectFilterProps {
  children: ReactNode
}

export function FirmDashboardQuickSelectFilter({
  children,
}: FirmDashboardQuickSelectFilterProps) {
  return (
    <div className={styles.root}>
      <h1>Quick select</h1>
      <div className={styles.cardsContainer}>{children}</div>
    </div>
  )
}

/**
 * The job statuses the quick select offers.
 */
export type QuickSelectOption = Extract<
  JobStatusFilterOption,
  'Onboarding' | 'InfoGathering' | 'InProgress' | 'Closed'
>

export interface FirmDashboardQuickSelectFilterCardProps {
  title: string
  option: QuickSelectOption
  count: number
  isActive: boolean
  onPress: () => void
}

export function FirmDashboardQuickSelectFilterCard({
  title,
  option,
  count,
  isActive,
  onPress,
}: FirmDashboardQuickSelectFilterCardProps) {
  return (
    <RawButton
      className={cx(styles.filterCardContainer, isActive && styles.active)}
      onPress={onPress}
    >
      <Card borderless>
        <CardContent compact>
          <div
            className={cx(
              styles.filterCardContent,
              match(option, {
                Onboarding:
                  styles['filterCardContentVariant-underConstruction'],
                InfoGathering: styles['filterCardContentVariant-infoGathering'],
                InProgress: styles['filterCardContentVariant-inProgress'],
                Closed: styles['filterCardContentVariant-completed'],
              }),
            )}
          >
            <div
              className={cx(
                styles.filterCardIcon,
                match(option, {
                  Onboarding: styles['filterCardIconVariant-underConstruction'],
                  InfoGathering: styles['filterCardIconVariant-infoGathering'],
                  InProgress: styles['filterCardIconVariant-inProgress'],
                  Closed: styles['filterCardIconVariant-completed'],
                }),
              )}
            >
              {match(option, {
                Onboarding: () => (
                  <Icon auto size={24} color="var(--red400)" name="pending" />
                ),
                InfoGathering: () => (
                  <Icon auto size={24} color="var(--purple700)" name="info" />
                ),
                InProgress: () => (
                  <Icon auto size={24} color="var(--blue400)" name="jobs" />
                ),
                Closed: () => (
                  <Icon
                    auto
                    size={24}
                    color="var(--green400)"
                    name="complete-job"
                  />
                ),
              })}
            </div>
            <h2>{title}</h2>
            <h1>{count}</h1>
          </div>
        </CardContent>
      </Card>
    </RawButton>
  )
}
